import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'dialogVariant',
})(({ theme, dialogVariant }) => {
  const variants = {
    default: {
      '& .MuiDialog-paper': {
        borderRadius: 0,
        minWidth: 400,
        maxWidth: 600,
      },
    },
    form: {
      '& .MuiDialog-paper': {
        borderRadius: 0,
        padding: theme.spacing(1),
        minWidth: 500,
        maxWidth: 600,
      },
    },
    large: {
      '& .MuiDialog-paper': {
        borderRadius: 0,
        padding: theme.spacing(0.25),
        minWidth: 650,
        maxWidth: 750,
        maxHeight: '90vh',
      },
    },
    fullWidth: {
      '& .MuiDialog-paper': {
        borderRadius: 0,
        minHeight: '600px',
      },
    },
  };

  return variants[dialogVariant] || variants.default;
});

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: '#DBEAFE',
  color: theme.palette.primary.main,
  margin: 0,
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 0,
}));

const StyledDialogContent = styled(DialogContent)(({ theme, dialogVariant }) => ({
  padding: theme.spacing(2, 3, 3, 3),
  ...(dialogVariant === 'form' && {
    padding: theme.spacing(6, 3, 3, 3), // Further increased top padding for better separation from header
    '& .MuiStack-root': {
      gap: theme.spacing(2), // Reduced spacing between form fields for more compact layout
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(0.5),
    },
  }),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  gap: theme.spacing(2),
}));

const EasyCheckDialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  variant = 'default',
  maxWidth = 'sm',
  fullWidth = true,
  showCloseButton = true,
  titleIcon,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  ...props
}) => {
  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') return;
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
    onClose(event, reason);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      dialogVariant={variant}
      {...props}
    >
      {title && (
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {titleIcon}
            <Typography variant="h6" component="span" sx={{ color: 'primary.main' }}>
              {title}
            </Typography>
          </Box>
          {showCloseButton && (
            <IconButton
              onClick={onClose}
              sx={{
                color: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </StyledDialogTitle>
      )}
      
      <StyledDialogContent dialogVariant={variant}>
        {children}
      </StyledDialogContent>
      
      {actions && (
        <StyledDialogActions>
          {actions}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

EasyCheckDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  actions: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'form', 'large', 'fullWidth']),
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  fullWidth: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  titleIcon: PropTypes.node,
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
};

export default EasyCheckDialog; 